import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../../../contexts/ThemeContext";
import { Icon } from "@iconify/react";

interface SearchBarProps {
  className?: string;
  onSearch?: (query: string) => void;
  initialValue?: string;
}

const SearchBar: React.FC<SearchBarProps> = ({
  className = "",
  onSearch,
  initialValue = "",
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [query, setQuery] = useState(initialValue);
  const [isFocused, setIsFocused] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      if (onSearch) {
        onSearch(query.trim());
      } else {
        const searchParams = new URLSearchParams();
        searchParams.set("term", query.trim());
        searchParams.set("contentType", "video"); // Default to video content type
        navigate(`/search?${searchParams.toString()}`);
      }
    }
  };

  return (
    <div
      style={{
        border:
          theme.mode === "dark"
            ? "0.5px solid rgba(var(--colors-border-primary-rgb), 0.5)"
            : "0.5px solid rgba(var(--colors-border-primary-rgb), 1)",
      }}
      className={`
        relative flex items-center gap-2
        h-10
        px-3
        rounded-full
        border
        ${
          theme.mode === "dark"
            ? "bg-surface-primary/10"
            : "bg-surface-primary/5"
        }
        hover:border-primary-500/30
        transition-all duration-200
        ${
          isFocused
            ? "ring-1 ring-offset-1 ring-primary-500 ring-opacity-40 ring-offset-background-primary dark:ring-offset-background-primary"
            : ""
        }
        ${className}
      `}
    >
      <Icon
        icon="ph:magnifying-glass-thin"
        className="w-5 h-5 text-text-secondary opacity-50"
      />
      <form onSubmit={handleSubmit} className="flex-1">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder={t("common.searchPlaceholder")}
          className="
            w-full
            bg-transparent
            text-sm
            text-text-primary
            placeholder-text-secondary/50
            focus:outline-none
          "
        />
      </form>
    </div>
  );
};

export default SearchBar;
